import { useAppConfig } from "../context/AppConfigContext";

// Singleton store for feature flags accessible from non-component files
let featureFlagsStore = null;

/**
 * Initialize the feature flags store
 * This should be called from a component that has access to the context
 */
export const setFeatureFlags = (flags) => {
  featureFlagsStore = flags;
};

/**
 * Get feature flags from the store
 * Falls back to window if store is not initialized
 */
export const getFeatureFlags = () => {
  return featureFlagsStore || {};
};

export const FeatureToggle = {
  memberListExtra: "REACT_APP_MEMBER_LIST_EXTRA_FIELDS",
  showActivityIdAtSummaryPage: "SHOW_ACTIVITY_ID_AT_SUMMARY_PAGE",
  isDetailedOutcomeEnabledInSummaryPage:
    "IS_DETAILED_OUTCOME_ENABLED_IN_SUMMARY_PAGE",
  sweepstakesInCampaignSummary: "SWEEPSTAKES_ACTIVITY_IN_CAMPAIGN_SUMMARY",
  rewardCategoriesInCampaignSummary: "REWARD_CATEGORIES_IN_CAMPAIGN_SUMMARY",
  showCampaignCardAfterActivityCompletionText:
    "SHOW_CAMPAIGN_CARD_AFTER_ACTIVITY_COMPLETION_TEXT",
  showNumberOfEntriesInCampaignCardText:
    "SHOW_NUMBER_OF_ENTRIES_IN_CAMPAIGN_CARD_TEXT",
  popoverSetupInCampaignSummary: "POPOVER_SETUP_IN_CAMPAIGN_SUMMARY",
  walletSetupInCampaignSummary: "WALLET_SETUP_IN_CAMPAIGN_SUMMARY",
  showPerformancesSideMenu: "SHOW_PERFORMANCES_SIDE_MENU",
  isBonusFlowEnabled: "REACT_APP_ENABLE_BONUS_FLOW",
  showWidgetEmbedCode: "SHOW_WIDGET_EMBED_CODE",
  ptrCodeGroupUpload: "PTR_CODE_GROUP_UPLOAD",
  categoryActivityRulesSetup: "CATEGORY_ACTIVITY_RULES_SETUP",
  searchByCampaignShortTitle: "REACT_APP_SEARCH_BY_CAMPAIGN_SHORT_TITLE",
  displayRewardsCatalogs: "REACT_APP_DISPLAY_REWARDS_CATALOG",
  showContentManagmentSideMenu: "REACT_APP_SHOW_CONTENT_MANAGEMENT_SIDE_MENU",
  showCodeGroupSideMenu: "REACT_APP_SHOW_CODE_GROUP_SIDE_MENU",
  showLeaderboards: "REACT_APP_LEADERBOARDS",
  isLogNoteModalEnabled: "REACT_APP_ENABLE_LOG_NOTE_MODAL",
  isAnonymizeUserEnabled: "REACT_APP_ENABLE_ANONYMIZE_USER",
  showMemberDetailSidePanel: "REACT_APP_SHOW_MEMBER_DETAIL_SIDE_PANEL",
  showMemberParticipationHistory: "REACT_APP_SHOW_MEMBER_PARTICIPATION_HISTORY",
  enableMultiFileDownload: "REACT_APP_ENABLE_MULTI_FILE_DOWNLOAD",
  enableNewMemberSearch: "REACT_APP_ENABLE_NEW_MEMBER_SEARCH",
  enableBannerContentSlug: "REACT_APP_ENABLE_BANNER_CONTENT_SLUG",
  enableCountries: "REACT_APP_ENABLE_COUNTRIES",
  disablePlatformSegments: "REACT_APP_DISABLE_PLATFORM_SEGMENTS",
  useLegacyExportImport: "REACT_APP_USE_LEGACY_EXPORT_IMPORT",
  showCampaignShortTitle: "REACT_APP_SHOW_CAMPAIGN_SHORT_TITLE",
  enableUnlimitedPartnerCode: "REACT_APP_ENABLE_UNLIMITED_PARTNER_CODE",
  showCampaignApprovalButton: "REACT_APP_SHOW_CAMPAIGN_APPROVAL_BUTTON",
  enableActivityDragAndDrop: "REACT_APP_ENABLE_ACTIVITY_DRAG_AND_DROP",
  enableActivityNameFilter: "REACT_APP_ENABLE_ACTIVITY_NAME_FILTER",
  enableRestructureNavigateMenu: "REACT_APP_ENABLE_RESTRUCTURE_NAVIGATE_MENU",
  enableSingleCountryPreselection: "REACT_APP_ENABLE_SINGLE_COUNTRY_PRESELECTION",
  enableRewardGroupsInCampaignListing:
    "REACT_APP_ENABLE_REWARD_GROUPS_IN_CAMPAIGN_LISTING",
  enableNewConfigLimitCodeScan: "REACT_APP_ENABLE_NEW_CONFIG_LIMIT_CODE_SCAN",
  enableRewardCategoryFields: "REACT_APP_ENABLE_REWARD_CATEGORY_FIELDS",
  disableInventoryRolloverByDefault:
    "REACT_APP_DISABLE_INVENTORY_ROLLOVER_BY_DEFAULT",
  enableRewardDefinitionInventory: "REACT_APP_ENABLE_REWARD_DEFINITION_INVENTORY",
  showNewDesignOfRulesCategories: "REACT_APP_SHOW_NEW_DESIGN_OF_RULES_CATEGORIES",
  activityLevelCategorizeRules:
    "REACT_APP_SHOW_NEW_DESIGN_OF_RULES_CATEGORIES_ACTIVITY_LIMITS",
  enableActivityLabel: "REACT_APP_ENABLE_ACTIVITY_LABEL",
  showKeywordCategory: "REACT_APP_SHOW_KEYWORD_CATEGORY",
  enableMutuallyExclusiveOutcomes: "REACT_APP_ENABLE_MUTUALLY_EXCLUSIVE_OUTCOMES",
  enableB2b2cCampaign: "REACT_APP_ENABLE_B2B2C_CAMPAIGN",
  showConfigurationHistoryLogs: "REACT_APP_SHOW_CONFIGURATION_HISTORY_LOGS",
  enableNewVersionContentStudio: "REACT_APP_ENABLE_NEW_VERSION_CONTENT_STUDIO",
  enableOptionalOutcomeRules: "REACT_APP_ENABLE_OPTIONAL_OUTCOME_RULES",
};

/**
 * Check if a feature is enabled
 * Can be used in both components and plain JS files
 * Checks: 1) featureFlagsStore (from context), 2) window object
 */
export const isFeatureEnabled = (flagKey) => {
  // First check the store (populated from context)
  let ftValue = featureFlagsStore?.[flagKey];

  // Fallback to window if not in store
  if (ftValue === undefined) {
    ftValue = window?.[flagKey];
  }

  if (ftValue === undefined) {
    console.warn(`[FT] Feature "${flagKey}" is not defined!`);
  }

  return ftValue === "true" || ftValue === true;
};

// New hook for component-based feature toggle usage
export const useFeatureToggle = (featureKey) => {
  const { featureFlags, loading } = useAppConfig();

  // Sync feature flags to the store whenever they change
  if (featureFlags && !loading) {
    setFeatureFlags(featureFlags);
  }

  if (loading) {
    return false;
  }

  // First check dynamic toggles from context, fallback to window
  const value = featureFlags?.[featureKey] ?? window?.[featureKey];
  if (value === undefined) {
    console.warn(`[FT] Feature "${featureKey}" is not defined!`);
  }
  return value === "true" || value === true;
};
